/**
 * SieAccountingBasisPage - Detail page for "Redovisningsgrund (utifrån SIE-fil)" module
 * 
 * This page is accessed by clicking the module card in CaseDetailPage.
 * It contains tabs for different SIE-related functions.
 * 
 * Supports multi-file upload (up to 50 files) for merging multiple SIE files
 * (e.g., one per voucher series).
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSieMasterData, useInvalidateSieMasterData } from '@/hooks/useSieMasterData';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  ArrowLeft,
  Upload, 
  FileSpreadsheet, 
  Building2, 
  Calendar, 
  TrendingUp, 
  Scale,
  CheckCircle,
  AlertCircle,
  Loader2,
  BarChart3,
  GitCompare,
  BookOpen,
  ArrowLeftRight,
  Coins,
  Sparkles,
  Files,
  Merge,
  Info,
  AlertTriangle,
} from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { useDropzone } from 'react-dropzone';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { VoucherListModal } from '@/components/VoucherListModal';
import { AccountVouchersModal } from '@/components/AccountVouchersModal';
import { 
  YearComparisonView, 
  TrendCharts, 
  VarianceAnalysis, 
  KeyRatiosComparison,
  CashFlowAnalysis,
  type SIEProcessedData,
} from '@/components/sie';
import { SieFileStagingView, type StagedFile } from '@/components/sie/SieFileStagingView';
import { AnalyticalReviewTab } from '@/components/sie/analytical-review';
import { 
  formatAmount,
  formatAmountWithDisplayFormat,
  determineOptimalUnit,
  type AmountUnit,
  type DisplayFormat,
} from '@/components/sie/utils';

// Re-export types from sie/types.ts for backward compatibility
import type { 
  SIEMetadata, 
  IncomeStatementData, 
  MonthlyIncomeData, 
  BalanceSheetData, 
  StatisticsData, 
  SIEVoucher,
  SIEAccount,
  SIEDataQuality,
} from '@/components/sie/types';
import type { MaterialityThresholds } from '@/lib/modules/types';

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('sv-SE');
  } catch {
    return dateStr;
  }
}

const MONTH_NAMES: Record<string, string> = {
  '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
  '05': 'Maj', '06': 'Jun', '07': 'Jul', '08': 'Aug',
  '09': 'Sep', '10': 'Okt', '11': 'Nov', '12': 'Dec',
};

function getMonthShortName(month: string): string {
  const monthNum = month.split('-')[1];
  return MONTH_NAMES[monthNum] || monthNum;
}

export function SieAccountingBasisPage() {
  const { id: auditId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Use shared React Query hook for SIE data (syncs across modules)
  const { 
    data: sieQueryData, 
    isLoading: isSieLoading, 
    error: sieError 
  } = useSieMasterData(auditId);
  const invalidateSieMasterData = useInvalidateSieMasterData();
  
  const [isProcessing, setIsProcessing] = useState(false);
  // Local state derived from query data (for backward compatibility with existing code)
  const [processedData, setProcessedData] = useState<SIEProcessedData | null>(null);
  const [previousYearData, setPreviousYearData] = useState<SIEProcessedData | null>(null);
  const [activeTab, setActiveTab] = useState('upload');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
  const [uploadingYear, setUploadingYear] = useState<0 | -1>(0);
  
  // Multi-file upload state (current year)
  const [multiFileMode, setMultiFileMode] = useState(false);
  const [stagedFilesCurrentYear, setStagedFilesCurrentYear] = useState<StagedFile[]>([]);
  const [mergeInfoCurrentYear, setMergeInfoCurrentYear] = useState<{
    totalFiles: number;
    sourceFiles: Array<{ filename?: string; voucherSeries: string[]; voucherCount: number }>;
    duplicatesSkipped: number;
  } | null>(null);
  
  // Multi-file upload state (previous year)
  const [stagedFilesPreviousYear, setStagedFilesPreviousYear] = useState<StagedFile[]>([]);
  const [mergeInfoPreviousYear, setMergeInfoPreviousYear] = useState<{
    totalFiles: number;
    sourceFiles: Array<{ filename?: string; voucherSeries: string[]; voucherCount: number }>;
    duplicatesSkipped: number;
  } | null>(null);
  
  const fileInputCurrentYearRef = useRef<HTMLInputElement>(null);
  const fileInputPreviousYearRef = useRef<HTMLInputElement>(null);
  
  // Backward compatibility alias for existing code
  const stagedFiles = stagedFilesCurrentYear;
  const setStagedFiles = setStagedFilesCurrentYear;
  const mergeInfo = mergeInfoCurrentYear;
  const setMergeInfo = setMergeInfoCurrentYear;
  const fileInputRef = fileInputCurrentYearRef;
  
  // Classification schema state
  const [availableSchemas, setAvailableSchemas] = useState<{
    id: string;
    schema_name: string;
    schema_version: string;
    is_default: boolean;
    description?: string;
  }[]>([]);
  const [selectedSchemaId, setSelectedSchemaId] = useState<string | undefined>(undefined);
  const [loadedSchemaName, setLoadedSchemaName] = useState<string | null>(null);
  
  // Account drill-down state
  const [selectedAccount, setSelectedAccount] = useState<{ account: string; name: string } | null>(null);
  const [isAccountVouchersModalOpen, setIsAccountVouchersModalOpen] = useState(false);
  
  // Amount unit selection (kr, tkr, or auto) - for input interpretation
  const [selectedAmountUnit, setSelectedAmountUnit] = useState<AmountUnit>('auto');
  
  // Display format selection - for output formatting (kr, tkr, Mkr, or auto)
  const [displayFormat, setDisplayFormat] = useState<DisplayFormat>('auto');
  
  // Materiality thresholds for variance analysis
  const [materialityThresholds, setMaterialityThresholds] = useState<MaterialityThresholds | null>(null);
  
  // Max files for multi-file upload
  const MAX_FILES = 50;
  const DIRECT_UPLOAD_LIMIT = 5 * 1024 * 1024; // 5MB
  
  // Handle account click for drill-down
  const handleAccountClick = (account: string, name: string) => {
    setSelectedAccount({ account, name });
    setIsAccountVouchersModalOpen(true);
  };
  
  // Fetch available classification schemas
  useEffect(() => {
    const fetchSchemas = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('list-classification-schemas');
        if (error) {
          console.error('[SieAccountingBasisPage] Error fetching schemas:', error);
          return;
        }
        if (data?.schemas) {
          setAvailableSchemas(data.schemas);
          // Set default schema as selected
          const defaultSchema = data.schemas.find((s: { is_default: boolean }) => s.is_default);
          if (defaultSchema) {
            setSelectedSchemaId(defaultSchema.id);
          }
        }
      } catch (err) {
        console.error('[SieAccountingBasisPage] Error fetching schemas:', err);
      }
    };
    fetchSchemas();
  }, []);

  // Sync SIE data from React Query hook to local state
  // This provides automatic updates when data changes in other modules
  useEffect(() => {
    if (sieQueryData) {
      console.log('[SieAccountingBasisPage] Syncing data from React Query cache');
      
      if (sieQueryData.currentYear) {
        setProcessedData(sieQueryData.currentYear);
        setActiveTab('overview');
      }
      if (sieQueryData.previousYear) {
        setPreviousYearData(sieQueryData.previousYear);
      }
      if (sieQueryData.classificationSchemaName) {
        setLoadedSchemaName(sieQueryData.classificationSchemaName);
      }
      if (sieQueryData.mergeMetadata) {
        setMergeInfo(sieQueryData.mergeMetadata);
      }
    }
    
    // Sync loading state
    if (!isSieLoading) {
      setIsLoading(false);
    }
  }, [sieQueryData, isSieLoading]);

  // Load materiality thresholds from audit (separate from SIE data)
  useEffect(() => {
    const loadMaterialityThresholds = async () => {
      if (!auditId) return;
      
      try {
        const { data } = await supabase
          .from('audits')
          .select('data_input')
          .eq('id', auditId)
          .single();

        const dataInput = data?.data_input as { thresholds?: MaterialityThresholds } | null;
        if (dataInput?.thresholds) {
          console.log('[SieAccountingBasisPage] Loaded materiality thresholds:', dataInput.thresholds);
          setMaterialityThresholds(dataInput.thresholds);
        }
      } catch (error) {
        console.error('[SieAccountingBasisPage] Error loading materiality thresholds:', error);
      }
    };

    loadMaterialityThresholds();
  }, [auditId]);

  const processFile = useCallback(async (file: File, fiscalYearIndex: 0 | -1 = 0) => {
    if (!auditId) return;
    
    setIsProcessing(true);
    setUploadingYear(fiscalYearIndex);
    
    try {
      // Read file with proper encoding for Swedish characters
      // SIE standard specifies IBM PC 8-bit (CP437) but most modern Swedish systems use ISO-8859-1 or Windows-1252
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      
      // CP437 (IBM PC 8-bit) decoding table for Swedish characters
      // This is the encoding specified by SIE standard
      const cp437ToUnicode: Record<number, string> = {
        0x84: 'ä', // 132
        0x86: 'å', // 134
        0x8E: 'Ä', // 142
        0x8F: 'Å', // 143
        0x94: 'ö', // 148
        0x99: 'Ö', // 153
        0x81: 'ü', // 129
        0x9A: 'Ü', // 154
        0x82: 'é', // 130
        0x90: 'É', // 144
      };
      
      // Function to decode CP437 to Unicode
      const decodeCP437 = (data: Uint8Array): string => {
        let result = '';
        for (let i = 0; i < data.length; i++) {
          const byte = data[i];
          if (cp437ToUnicode[byte]) {
            result += cp437ToUnicode[byte];
          } else if (byte < 128) {
            result += String.fromCharCode(byte);
          } else {
            // For other extended ASCII, use Windows-1252 as fallback
            result += String.fromCharCode(byte);
          }
        }
        return result;
      };
      
      // Check for UTF-8 BOM (EF BB BF)
      const hasUtf8BOM = bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF;
      
      let content: string;
      
      if (hasUtf8BOM) {
        // Explicit UTF-8 file
        content = new TextDecoder('utf-8').decode(buffer);
        console.log('[SieAccountingBasisPage] Using UTF-8 encoding (BOM detected)');
      } else {
        // Check if it looks like CP437 encoding (contains bytes 0x84, 0x86, 0x94 which are Swedish chars in CP437)
        const hasCP437SwedishBytes = bytes.some(b => b === 0x84 || b === 0x86 || b === 0x94 || b === 0x8E || b === 0x8F || b === 0x99);
        
        // Check if it looks like ISO-8859-1 (contains bytes 0xE4, 0xE5, 0xF6 which are Swedish chars in ISO-8859-1)
        const hasISO88591SwedishBytes = bytes.some(b => b === 0xE4 || b === 0xE5 || b === 0xF6 || b === 0xC4 || b === 0xC5 || b === 0xD6);
        
        if (hasCP437SwedishBytes && !hasISO88591SwedishBytes) {
          // Use CP437 decoding for SIE standard files
          content = decodeCP437(bytes);
          console.log('[SieAccountingBasisPage] Using CP437 encoding (SIE standard)');
        } else if (hasISO88591SwedishBytes) {
          // Use ISO-8859-1 for modern Swedish files
          content = new TextDecoder('iso-8859-1').decode(buffer);
          console.log('[SieAccountingBasisPage] Using ISO-8859-1 encoding');
        } else {
          // Fallback: try Windows-1252 which is very similar to ISO-8859-1
          content = new TextDecoder('windows-1252').decode(buffer);
          console.log('[SieAccountingBasisPage] Using Windows-1252 encoding (fallback)');
        }
      }
      
      console.log('[SieAccountingBasisPage] Processing SIE file:', file.name, 'size:', content.length);

      const { data, error } = await supabase.functions.invoke('process-sie-file', {
        body: {
          auditId,
          sieContent: content,
          saveToDatabase: true,
          fiscalYearIndex,
          classificationSchemaId: selectedSchemaId,
        },
      });

      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || 'Unknown error processing SIE file');

      console.log('[SieAccountingBasisPage] Processed successfully:', data.data);
      
      // Add vouchers to the response data
      const processedResult: SIEProcessedData = {
        ...data.data,
        vouchers: data.data.vouchers || [],
        accounts: data.data.accounts || [],
      };
      
      if (fiscalYearIndex === 0) {
        setProcessedData(processedResult);
        setActiveTab('overview');
        // Store which schema was used
        if (data.data.classificationSchemaName) {
          setLoadedSchemaName(data.data.classificationSchemaName);
        }
      } else {
        setPreviousYearData(processedResult);
      }

      const yearLabel = fiscalYearIndex === 0 ? 'Innevarande år' : 'Föregående år';
      toast({
        title: `SIE-fil bearbetad (${yearLabel})`,
        description: `${data.data.aggregated.statistics.totalAccounts} konton och ${data.data.aggregated.statistics.totalVouchers} verifikationer hittades`,
      });
      
      // Invalidate shared cache to sync with other modules
      invalidateSieMasterData(auditId);

    } catch (error) {
      console.error('[SieAccountingBasisPage] Error:', error);
      toast({
        title: 'Fel vid bearbetning',
        description: error instanceof Error ? error.message : 'Kunde inte bearbeta SIE-filen',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  }, [auditId, toast, selectedSchemaId, invalidateSieMasterData]);

  // Helper to decode SIE file content with proper encoding
  const decodeSieFile = useCallback(async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    
    const cp437ToUnicode: Record<number, string> = {
      0x84: 'ä', 0x86: 'å', 0x8E: 'Ä', 0x8F: 'Å',
      0x94: 'ö', 0x99: 'Ö', 0x81: 'ü', 0x9A: 'Ü',
      0x82: 'é', 0x90: 'É',
    };
    
    const decodeCP437 = (data: Uint8Array): string => {
      let result = '';
      for (let i = 0; i < data.length; i++) {
        const byte = data[i];
        if (cp437ToUnicode[byte]) {
          result += cp437ToUnicode[byte];
        } else if (byte < 128) {
          result += String.fromCharCode(byte);
        } else {
          result += String.fromCharCode(byte);
        }
      }
      return result;
    };
    
    const hasUtf8BOM = bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF;
    
    if (hasUtf8BOM) {
      return new TextDecoder('utf-8').decode(buffer);
    }
    
    const hasCP437SwedishBytes = bytes.some(b => b === 0x84 || b === 0x86 || b === 0x94 || b === 0x8E || b === 0x8F || b === 0x99);
    const hasISO88591SwedishBytes = bytes.some(b => b === 0xE4 || b === 0xE5 || b === 0xF6 || b === 0xC4 || b === 0xC5 || b === 0xD6);
    
    if (hasCP437SwedishBytes && !hasISO88591SwedishBytes) {
      return decodeCP437(bytes);
    } else if (hasISO88591SwedishBytes) {
      return new TextDecoder('iso-8859-1').decode(buffer);
    }
    return new TextDecoder('windows-1252').decode(buffer);
  }, []);

  // Multi-file processing - now parameterized by fiscal year
  const processMultipleFilesForYear = useCallback(async (
    files: StagedFile[],
    fiscalYearIndex: 0 | -1,
    setFiles: React.Dispatch<React.SetStateAction<StagedFile[]>>,
    setMergeInfoFn: React.Dispatch<React.SetStateAction<{
      totalFiles: number;
      sourceFiles: Array<{ filename?: string; voucherSeries: string[]; voucherCount: number }>;
      duplicatesSkipped: number;
    } | null>>
  ) => {
    if (!auditId || files.length === 0) return;
    
    setIsProcessing(true);
    const yearLabel = fiscalYearIndex === 0 ? 'innevarande år' : 'föregående år';
    
    try {
      const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);
      console.log(`[SieAccountingBasisPage] Processing ${files.length} files for ${yearLabel}, total size: ${totalSize}`);
      
      // Update all files to processing state
      setFiles(prev => prev.map(f => ({ ...f, status: 'processing' as const, progress: 0 })));
      
      // Read all file contents
      const fileContents: { filename: string; content: string }[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const stagedFile = files[i];
        
        // Update progress for this file
        setFiles(prev => prev.map(f => 
          f.id === stagedFile.id ? { ...f, progress: 50 } : f
        ));
        
        const content = await decodeSieFile(stagedFile.file);
        fileContents.push({
          filename: stagedFile.file.name,
          content,
        });
        
        // Mark as done reading
        setFiles(prev => prev.map(f => 
          f.id === stagedFile.id ? { ...f, progress: 100 } : f
        ));
      }
      
      console.log(`[SieAccountingBasisPage] Sending ${fileContents.length} files to batch processor for ${yearLabel}`);
      
      // Call batch processing edge function
      const { data, error } = await supabase.functions.invoke('process-sie-files-batch', {
        body: {
          auditId,
          sieContents: fileContents,
          saveToDatabase: true,
          fiscalYearIndex,
          classificationSchemaId: selectedSchemaId,
        },
      });
      
      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || 'Unknown error processing SIE files');
      
      console.log(`[SieAccountingBasisPage] Batch processed successfully for ${yearLabel}:`, data.data);
      
      // Mark all files as done
      setFiles(prev => prev.map(f => ({ ...f, status: 'done' as const, progress: 100 })));
      
      // Create processed data from response
      const processedResult: SIEProcessedData = {
        ...data.data,
        vouchers: data.data.vouchers || [],
        accounts: data.data.accounts || [],
      };
      
      // Update state based on fiscal year
      if (fiscalYearIndex === 0) {
        setProcessedData(processedResult);
        setActiveTab('overview');
      } else {
        setPreviousYearData(processedResult);
      }
      
      // Store merge info for display
      if (data.data.mergeInfo) {
        setMergeInfoFn({
          totalFiles: data.data.mergeInfo.totalFiles,
          sourceFiles: data.data.mergeInfo.sourceFiles,
          duplicatesSkipped: data.data.mergeInfo.statistics.duplicateVouchersSkipped,
        });
      }
      
      if (data.data.classificationSchemaName) {
        setLoadedSchemaName(data.data.classificationSchemaName);
      }
      
      const seriesInfo = data.data.mergeInfo?.sourceFiles
        .map((sf: { voucherSeries: string[] }) => sf.voucherSeries.join(', '))
        .filter(Boolean)
        .join('; ');
      
      toast({
        title: `${files.length} SIE-filer sammanslagna (${yearLabel})`,
        description: `${data.data.aggregated.statistics.totalVouchers} verifikationer${seriesInfo ? ` (${seriesInfo})` : ''}`,
      });
      
      // Clear staged files after success
      setFiles([]);
      
      // Invalidate shared cache to sync with other modules
      invalidateSieMasterData(auditId);
      
    } catch (error) {
      console.error(`[SieAccountingBasisPage] Multi-file error for ${yearLabel}:`, error);
      
      // Mark files with error
      setFiles(prev => prev.map(f => ({
        ...f,
        status: 'error' as const,
        error: error instanceof Error ? error.message : 'Bearbetning misslyckades',
      })));
      
      toast({
        title: `Fel vid sammanslagning (${yearLabel})`,
        description: error instanceof Error ? error.message : 'Kunde inte bearbeta SIE-filerna',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  }, [auditId, decodeSieFile, toast, selectedSchemaId, invalidateSieMasterData]);
  
  // Convenience wrappers for each year
  const processMultipleFiles = useCallback(() => {
    return processMultipleFilesForYear(
      stagedFilesCurrentYear, 
      0, 
      setStagedFilesCurrentYear, 
      setMergeInfoCurrentYear
    );
  }, [processMultipleFilesForYear, stagedFilesCurrentYear]);
  
  const processMultipleFilesPreviousYear = useCallback(() => {
    return processMultipleFilesForYear(
      stagedFilesPreviousYear, 
      -1, 
      setStagedFilesPreviousYear, 
      setMergeInfoPreviousYear
    );
  }, [processMultipleFilesForYear, stagedFilesPreviousYear]);

  // Add files to staging (current year)
  const handleFilesSelectedCurrentYear = useCallback((files: File[]) => {
    const newFiles: StagedFile[] = files.map(file => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      status: 'pending' as const,
      progress: 0,
    }));
    
    setStagedFilesCurrentYear(prev => {
      const combined = [...prev, ...newFiles];
      return combined.slice(0, MAX_FILES);
    });
  }, [MAX_FILES]);
  
  // Add files to staging (previous year)
  const handleFilesSelectedPreviousYear = useCallback((files: File[]) => {
    const newFiles: StagedFile[] = files.map(file => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      status: 'pending' as const,
      progress: 0,
    }));
    
    setStagedFilesPreviousYear(prev => {
      const combined = [...prev, ...newFiles];
      return combined.slice(0, MAX_FILES);
    });
  }, [MAX_FILES]);
  
  // Backward compatibility alias
  const handleFilesSelected = handleFilesSelectedCurrentYear;

  // Remove a file from staging (current year)
  const handleRemoveFileCurrentYear = useCallback((id: string) => {
    setStagedFilesCurrentYear(prev => prev.filter(f => f.id !== id));
  }, []);
  
  // Remove a file from staging (previous year)
  const handleRemoveFilePreviousYear = useCallback((id: string) => {
    setStagedFilesPreviousYear(prev => prev.filter(f => f.id !== id));
  }, []);
  
  // Backward compatibility alias
  const handleRemoveFile = handleRemoveFileCurrentYear;

  // Clear all staged files (current year)
  const handleClearAllCurrentYear = useCallback(() => {
    setStagedFilesCurrentYear([]);
  }, []);
  
  // Clear all staged files (previous year)
  const handleClearAllPreviousYear = useCallback(() => {
    setStagedFilesPreviousYear([]);
  }, []);
  
  // Backward compatibility alias
  const handleClearAll = handleClearAllCurrentYear;

  // Open file picker for multi-file (current year)
  const handleAddMoreFilesCurrentYear = useCallback(() => {
    fileInputCurrentYearRef.current?.click();
  }, []);
  
  // Open file picker for multi-file (previous year)
  const handleAddMoreFilesPreviousYear = useCallback(() => {
    fileInputPreviousYearRef.current?.click();
  }, []);
  
  // Backward compatibility alias
  const handleAddMoreFiles = handleAddMoreFilesCurrentYear;

  // Dropzone for multi-file (current year)
  const { getRootProps: getMultiRootPropsCurrentYear, getInputProps: getMultiInputPropsCurrentYear, isDragActive: isMultiDragActiveCurrentYear } = useDropzone({
    onDrop: handleFilesSelectedCurrentYear,
    accept: {
      'text/plain': ['.se', '.sie', '.si'],
      'application/octet-stream': ['.se', '.sie', '.si'],
    },
    maxFiles: MAX_FILES,
    disabled: isProcessing,
    noClick: stagedFilesCurrentYear.length > 0,
  });
  
  // Dropzone for multi-file (previous year)
  const { getRootProps: getMultiRootPropsPreviousYear, getInputProps: getMultiInputPropsPreviousYear, isDragActive: isMultiDragActivePreviousYear } = useDropzone({
    onDrop: handleFilesSelectedPreviousYear,
    accept: {
      'text/plain': ['.se', '.sie', '.si'],
      'application/octet-stream': ['.se', '.sie', '.si'],
    },
    maxFiles: MAX_FILES,
    disabled: isProcessing,
    noClick: stagedFilesPreviousYear.length > 0,
  });
  
  // Backward compatibility aliases
  const getMultiRootProps = getMultiRootPropsCurrentYear;
  const getMultiInputProps = getMultiInputPropsCurrentYear;
  const isMultiDragActive = isMultiDragActiveCurrentYear;

  // Original single-file dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        processFile(acceptedFiles[0]);
      }
    },
    accept: {
      'text/plain': ['.se', '.sie', '.si'],
      'application/octet-stream': ['.se', '.sie', '.si'],
    },
    maxFiles: 1,
    disabled: isProcessing,
  });

  // Calculate sorted months for the table
  const sortedMonths = useMemo(() => {
    if (!processedData?.aggregated.statistics.vouchersByMonth) return [];
    return Object.keys(processedData.aggregated.statistics.vouchersByMonth).sort();
  }, [processedData]);

  // Create account names lookup map for displaying account names in voucher modals
  const accountNames = useMemo(() => {
    if (!processedData?.accounts) return {};
    return processedData.accounts.reduce((map, acc) => {
      map[acc.number] = acc.name;
      return map;
    }, {} as Record<string, string>);
  }, [processedData?.accounts]);

  // Get all unique accounts from income statement (full BAS structure)
  const allAccounts = useMemo(() => {
    if (!processedData) return { 
      revenue: [], 
      expenses: [], 
      financialIncome: [],
      financialExpenses: [],
      appropriations: [],
      taxes: []
    };
    return {
      revenue: processedData.aggregated.incomeStatement.revenue,
      expenses: processedData.aggregated.incomeStatement.expenses,
      financialIncome: processedData.aggregated.incomeStatement.financialIncome || [],
      financialExpenses: processedData.aggregated.incomeStatement.financialExpenses || [],
      appropriations: processedData.aggregated.incomeStatement.appropriations || [],
      taxes: processedData.aggregated.incomeStatement.taxes || [],
    };
  }, [processedData]);

  // Calculate optimal display unit for tables based on key amounts
  const tableAutoUnit = useMemo(() => {
    if (!processedData) return 'tkr' as const;
    const is = processedData.aggregated.incomeStatement;
    const bs = processedData.aggregated.balanceSheet;
    const keyAmounts = [
      is.totalRevenue,
      is.totalExpenses,
      is.operatingResult,
      is.netResult,
      bs.totalAssets,
      bs.totalLiabilities,
      bs.totalEquity,
    ].filter(v => v !== undefined && v !== null);
    return determineOptimalUnit(keyAmounts);
  }, [processedData]);

  // Handle month click for voucher modal
  const handleMonthClick = (month: string) => {
    setSelectedMonth(month);
    setIsVoucherModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const currentYear = processedData?.metadata.fiscalYears.find(fy => fy.index === 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/cases/${auditId}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Redovisningsgrund (utifrån SIE-fil)</h1>
          <p className="text-muted-foreground">
            Analysera och visualisera bokföringsdata från SIE-filer
          </p>
        </div>
        <Badge 
          variant="outline" 
          className={processedData ? 'bg-success/10 text-success border-success/20' : 'bg-muted text-muted-foreground'}
        >
          {processedData ? (
            <>
              <CheckCircle className="h-3 w-3 mr-1" />
              SIE-fil laddad
            </>
          ) : (
            'Ej uppladdad'
          )}
        </Badge>
      </div>

      {/* Data Quality Warning - Show prominently when vouchers are missing */}
      {processedData?.aggregated.dataQuality && 
       processedData.aggregated.dataQuality.source !== 'vouchers' && (
        <Alert variant="destructive" className="border-2 border-destructive bg-destructive/10">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle className="text-lg font-bold">
            Ofullständig SIE-fil – verifikationer saknas
          </AlertTitle>
          <AlertDescription className="text-base space-y-2">
            <p>
              Denna SIE-fil innehåller endast{' '}
              {processedData.aggregated.dataQuality.source === 'psaldo' 
                ? 'periodsaldon (månadstotaler)' 
                : 'årssaldon'}{' '}
              – inte individuella verifikationer.
            </p>
            <p>
              <strong>Konsekvens:</strong> Du kan se trender och totaler per månad, 
              men du kan <strong>inte granska enskilda transaktioner</strong>.
            </p>
            <p className="font-semibold">
              ⚠️ Begär en ny SIE4-export med verifikationer (SIE4E eller SIE4) 
              från kunden för fullständig revisionsgrund.
            </p>
            <div className="mt-3 text-sm text-muted-foreground">
              Täckningsgrad: {processedData.aggregated.dataQuality.voucherCoverage.toFixed(0)}% av intäkterna 
              finns i verifikationer
              {processedData.aggregated.dataQuality.missingAmount > 0 && (
                <> ({formatAmountWithDisplayFormat(processedData.aggregated.dataQuality.missingAmount, 'auto', tableAutoUnit)} saknas)</>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="relative w-full">
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-muted to-transparent z-10 rounded-l-md" />
          <TabsList className="inline-flex w-full overflow-x-auto scrollbar-hide gap-1 px-6">
            <TabsTrigger value="upload" className="gap-2 flex-shrink-0">
              <Upload className="h-4 w-4" />
              Ladda upp
            </TabsTrigger>
            <TabsTrigger value="overview" disabled={!processedData} className="gap-2 flex-shrink-0">
              <FileSpreadsheet className="h-4 w-4" />
              Översikt
            </TabsTrigger>
            <TabsTrigger value="income-statement" disabled={!processedData} className="gap-2 flex-shrink-0">
              <TrendingUp className="h-4 w-4" />
              Resultaträkning
            </TabsTrigger>
            <TabsTrigger value="balance-sheet" disabled={!processedData} className="gap-2 flex-shrink-0">
              <Scale className="h-4 w-4" />
              Balansräkning
            </TabsTrigger>
            <TabsTrigger value="cashflow" disabled={!processedData} className="gap-2 flex-shrink-0">
              <ArrowLeftRight className="h-4 w-4" />
              Kassaflöde
            </TabsTrigger>
            <TabsTrigger value="statistics" disabled={!processedData} className="gap-2 flex-shrink-0">
              <BarChart3 className="h-4 w-4" />
              Statistik
            </TabsTrigger>
            <TabsTrigger value="comparison" disabled={!processedData || !previousYearData} className="gap-2 flex-shrink-0">
              <GitCompare className="h-4 w-4" />
              Jämförelse
            </TabsTrigger>
            <TabsTrigger value="analytical-review" disabled={!processedData} className="gap-2 flex-shrink-0">
              <Sparkles className="h-4 w-4" />
              Analytisk granskning
            </TabsTrigger>
          </TabsList>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-muted to-transparent z-10 rounded-r-md" />
        </div>

        {/* Upload Tab - Dual upload */}
        <TabsContent value="upload" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Ladda upp SIE-filer</CardTitle>
                  <CardDescription>
                    {multiFileMode 
                      ? 'Ladda upp och slå samman flera SIE-filer (upp till 50 st, t.ex. en per verifikationsserie)'
                      : 'Ladda upp SIE-filer för innevarande och föregående räkenskapsår för jämförelse och analys'
                    }
                  </CardDescription>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="multi-file-mode" className="text-sm text-muted-foreground">
                          Multi-fil
                        </Label>
                        <Switch 
                          id="multi-file-mode"
                          checked={multiFileMode}
                          onCheckedChange={setMultiFileMode}
                          disabled={isProcessing}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      <p>Aktivera för att ladda upp och slå samman flera SIE-filer (t.ex. en per verifikationsserie, upp till 50 filer)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hidden file inputs for multi-file add more */}
              <input
                ref={fileInputCurrentYearRef}
                type="file"
                accept=".se,.sie,.si"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) {
                    handleFilesSelectedCurrentYear(Array.from(e.target.files));
                    e.target.value = '';
                  }
                }}
              />
              <input
                ref={fileInputPreviousYearRef}
                type="file"
                accept=".se,.sie,.si"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) {
                    handleFilesSelectedPreviousYear(Array.from(e.target.files));
                    e.target.value = '';
                  }
                }}
              />

              {/* Display Format Selector - Always visible */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <Coins className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <Label htmlFor="display-format-select-global" className="text-sm font-medium">
                    Visningsformat
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Välj hur beloppen ska visas efter uppladdning
                  </p>
                </div>
                <Select value={displayFormat} onValueChange={(v) => setDisplayFormat(v as DisplayFormat)}>
                  <SelectTrigger id="display-format-select-global" className="w-[240px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-background z-50">
                    <SelectItem value="auto">
                      <span>Automatisk (tkr/Mkr)</span>
                    </SelectItem>
                    <SelectItem value="kr">
                      <span>Kronor (kr)</span>
                    </SelectItem>
                    <SelectItem value="tkr">
                      <span>Tusentals (tkr)</span>
                    </SelectItem>
                    <SelectItem value="Mkr">
                      <span>Miljoner (Mkr)</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {multiFileMode ? (
                /* Multi-file upload mode - dual dropzones */
                <div className="space-y-6">
                  {/* Info box */}
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                    <Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium">Sammanslagning av flera SIE-filer</p>
                      <p className="text-muted-foreground">
                        Dra och släpp flera SIE-filer (t.ex. A-serie, B-serie, K-serie) för att slå samman dem till ett komplett dataset. 
                        Systemet validerar att alla filer har samma organisationsnummer och räkenskapsår.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Current Year Multi-file Upload */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">Innevarande räkenskapsår</h3>
                        {processedData && <CheckCircle className="h-4 w-4 text-success" />}
                      </div>
                      
                      {stagedFilesCurrentYear.length === 0 ? (
                        /* Empty state - dropzone */
                        <div
                          {...getMultiRootPropsCurrentYear()}
                          className={`
                            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                            transition-colors duration-200
                            ${isMultiDragActiveCurrentYear ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'}
                          `}
                        >
                          <input {...getMultiInputPropsCurrentYear()} />
                          <Files className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                          <p className="text-sm font-medium mb-1">
                            {isMultiDragActiveCurrentYear ? 'Släpp filerna här...' : 'Dra och släpp SIE-filer här'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            eller klicka för att välja (upp till {MAX_FILES} st)
                          </p>
                        </div>
                      ) : (
                        /* Staging view */
                        <SieFileStagingView
                          files={stagedFilesCurrentYear}
                          onRemoveFile={handleRemoveFileCurrentYear}
                          onClearAll={handleClearAllCurrentYear}
                          onAddMore={handleAddMoreFilesCurrentYear}
                          onProcess={processMultipleFiles}
                          isProcessing={isProcessing}
                          maxFiles={MAX_FILES}
                        />
                      )}

                      {/* Merge info after successful merge */}
                      {mergeInfoCurrentYear && processedData && (
                        <div className="p-3 bg-success/10 rounded-lg border border-success/20">
                          <div className="flex items-start gap-2">
                            <Merge className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                            <div className="flex-1 text-sm">
                              <p className="font-medium text-success">
                                {mergeInfoCurrentYear.totalFiles} filer sammanslagna
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {mergeInfoCurrentYear.sourceFiles.map((sf, i) => (
                                  <span key={i}>
                                    {sf.filename || `Fil ${i + 1}`}: {sf.voucherCount} ver.
                                    {i < mergeInfoCurrentYear.sourceFiles.length - 1 && ' • '}
                                  </span>
                                ))}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Previous Year Multi-file Upload */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">Föregående räkenskapsår</h3>
                        <Badge variant="secondary" className="text-xs">Valfritt</Badge>
                        {previousYearData && <CheckCircle className="h-4 w-4 text-success" />}
                      </div>
                      
                      {stagedFilesPreviousYear.length === 0 ? (
                        /* Empty state - dropzone */
                        <div
                          {...getMultiRootPropsPreviousYear()}
                          className={`
                            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                            transition-colors duration-200
                            ${isMultiDragActivePreviousYear ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'}
                          `}
                        >
                          <input {...getMultiInputPropsPreviousYear()} />
                          <Files className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                          <p className="text-sm font-medium mb-1">
                            {isMultiDragActivePreviousYear ? 'Släpp filerna här...' : 'Dra och släpp SIE-filer här'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            eller klicka för att välja (upp till {MAX_FILES} st)
                          </p>
                        </div>
                      ) : (
                        /* Staging view */
                        <SieFileStagingView
                          files={stagedFilesPreviousYear}
                          onRemoveFile={handleRemoveFilePreviousYear}
                          onClearAll={handleClearAllPreviousYear}
                          onAddMore={handleAddMoreFilesPreviousYear}
                          onProcess={processMultipleFilesPreviousYear}
                          isProcessing={isProcessing}
                          maxFiles={MAX_FILES}
                        />
                      )}

                      {/* Merge info after successful merge */}
                      {mergeInfoPreviousYear && previousYearData && (
                        <div className="p-3 bg-success/10 rounded-lg border border-success/20">
                          <div className="flex items-start gap-2">
                            <Merge className="h-4 w-4 text-success flex-shrink-0 mt-0.5" />
                            <div className="flex-1 text-sm">
                              <p className="font-medium text-success">
                                {mergeInfoPreviousYear.totalFiles} filer sammanslagna
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {mergeInfoPreviousYear.sourceFiles.map((sf, i) => (
                                  <span key={i}>
                                    {sf.filename || `Fil ${i + 1}`}: {sf.voucherCount} ver.
                                    {i < mergeInfoPreviousYear.sourceFiles.length - 1 && ' • '}
                                  </span>
                                ))}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Both years loaded message */}
                  {processedData && previousYearData && (
                    <div className="mt-2 p-4 bg-success/10 rounded-lg border border-success/20">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-success" />
                        <div className="flex-1">
                          <p className="font-medium">Båda åren laddade</p>
                          <p className="text-sm text-muted-foreground">
                            Gå till Jämförelse-fliken för att se avvikelser och trender
                          </p>
                        </div>
                        <Button onClick={() => setActiveTab('comparison')} className="gap-2">
                          <GitCompare className="h-4 w-4" />
                          Visa jämförelse
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Original single-file mode */
                <>
                  {/* Classification Schema Selector */}
                  <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                    <BookOpen className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="schema-select" className="text-sm font-medium">
                        Klassificeringsschema
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Välj vilken kontoplan som ska användas för att klassificera konton
                      </p>
                    </div>
                    <Select value={selectedSchemaId} onValueChange={setSelectedSchemaId}>
                      <SelectTrigger id="schema-select" className="w-[240px]">
                        <SelectValue placeholder="Välj schema..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSchemas.map(schema => (
                          <SelectItem key={schema.id} value={schema.id}>
                            <div className="flex items-center gap-2">
                              <span>{schema.schema_name}</span>
                              {schema.is_default && (
                                <Badge variant="secondary" className="text-xs py-0 px-1.5">Standard</Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    {/* Current Year Upload */}
                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-2 h-6">
                        <h3 className="font-medium">Innevarande räkenskapsår</h3>
                        {processedData && <CheckCircle className="h-4 w-4 text-success" />}
                      </div>
                      <div
                        onClick={() => {
                          if (!isProcessing) {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.se,.sie,.si';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) processFile(file, 0);
                            };
                            input.click();
                          }
                        }}
                        className={`
                          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer min-h-[120px] flex items-center justify-center
                          transition-colors duration-200 hover:border-primary/50 hover:bg-muted/30
                          ${processedData ? 'border-success/50 bg-success/5' : 'border-muted-foreground/25'}
                          ${isProcessing && uploadingYear === 0 ? 'pointer-events-none opacity-50' : ''}
                        `}
                      >
                        {isProcessing && uploadingYear === 0 ? (
                          <Loader2 className="h-8 w-8 text-primary animate-spin" />
                        ) : processedData ? (
                          <div className="text-sm">
                            <p className="font-medium">{processedData.metadata.companyName}</p>
                            <p className="text-muted-foreground">{processedData.metadata.orgNumber}</p>
                            <p className="text-xs text-muted-foreground mt-1">Klicka för att ersätta</p>
                          </div>
                        ) : (
                          <div>
                            <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm">Klicka för att välja fil</p>
                            <p className="text-xs text-primary/70 mt-2">
                              För flera SIE-filer, aktivera "Multi-fil" längst upp till höger
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-2 h-6">
                        <h3 className="font-medium">Föregående räkenskapsår</h3>
                        <Badge variant="secondary" className="text-xs">Valfritt</Badge>
                        {previousYearData && <CheckCircle className="h-4 w-4 text-success" />}
                      </div>
                      <div
                        onClick={() => {
                          if (!isProcessing) {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.se,.sie,.si';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) processFile(file, -1);
                            };
                            input.click();
                          }
                        }}
                        className={`
                          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer min-h-[120px] flex items-center justify-center
                          transition-colors duration-200 hover:border-primary/50 hover:bg-muted/30
                          ${previousYearData ? 'border-success/50 bg-success/5' : 'border-muted-foreground/25'}
                          ${isProcessing && uploadingYear === -1 ? 'pointer-events-none opacity-50' : ''}
                        `}
                      >
                        {isProcessing && uploadingYear === -1 ? (
                          <Loader2 className="h-8 w-8 text-primary animate-spin" />
                        ) : previousYearData ? (
                          <div className="text-sm">
                            <p className="font-medium">{previousYearData.metadata.companyName}</p>
                            <p className="text-muted-foreground">{previousYearData.metadata.orgNumber}</p>
                            <p className="text-xs text-muted-foreground mt-1">Klicka för att ersätta</p>
                          </div>
                        ) : (
                          <div>
                            <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm">Klicka för att välja fil</p>
                            <p className="text-xs text-muted-foreground">För årsjämförelse</p>
                            <p className="text-xs text-primary/70 mt-1">
                              För flera SIE-filer, aktivera "Multi-fil" längst upp till höger
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {processedData && previousYearData && (
                    <div className="mt-6 p-4 bg-success/10 rounded-lg border border-success/20">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-success" />
                        <div className="flex-1">
                          <p className="font-medium">Båda åren laddade</p>
                          <p className="text-sm text-muted-foreground">
                            Gå till Jämförelse-fliken för att se avvikelser och trender
                          </p>
                        </div>
                        <Button onClick={() => setActiveTab('comparison')} className="gap-2">
                          <GitCompare className="h-4 w-4" />
                          Visa jämförelse
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {processedData && (
            <>
              {/* Company Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Företagsinformation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 md:grid-cols-4">
                    <div className="flex items-start gap-3">
                      <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Företag</p>
                        <p className="font-medium">{processedData.metadata.companyName || '-'}</p>
                        <p className="text-xs text-muted-foreground">{processedData.metadata.orgNumber || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Räkenskapsår</p>
                        <p className="font-medium">
                          {currentYear 
                            ? `${formatDate(currentYear.start)} – ${formatDate(currentYear.end)}`
                            : '-'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <FileSpreadsheet className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Innehåll</p>
                        <p className="font-medium">
                          {processedData.aggregated.statistics.totalAccounts} konton
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {processedData.aggregated.statistics.totalVouchers} verifikationer, {processedData.aggregated.statistics.totalTransactions} transaktioner
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <BookOpen className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm text-muted-foreground">Klassificeringsschema</p>
                        <p className="font-medium">{loadedSchemaName || 'BAS 2024'}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Cards */}
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Resultaträkning (sammanfattning)
                    </CardTitle>
                  </CardHeader>
                <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Intäkter (3xxx)</span>
                      <span className="font-medium text-green-600 whitespace-nowrap">{formatAmountWithDisplayFormat(processedData.aggregated.incomeStatement.totalRevenue, displayFormat, tableAutoUnit)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Kostnader (4-8xxx)</span>
                      <span className="font-medium text-red-600 whitespace-nowrap">{formatAmountWithDisplayFormat(-processedData.aggregated.incomeStatement.totalExpenses, displayFormat, tableAutoUnit)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-sm font-medium">
                      <span>Resultat</span>
                      <span className={`whitespace-nowrap ${processedData.aggregated.incomeStatement.netResult >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatAmountWithDisplayFormat(processedData.aggregated.incomeStatement.netResult, displayFormat, tableAutoUnit)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Scale className="h-4 w-4" />
                      Balansräkning (sammanfattning)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tillgångar (1xxx)</span>
                      <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(processedData.aggregated.balanceSheet.totalAssets, displayFormat, tableAutoUnit)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Skulder (24-29xx)</span>
                      <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(processedData.aggregated.balanceSheet.totalLiabilities, displayFormat, tableAutoUnit)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Eget kapital (20-23xx)</span>
                      <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(processedData.aggregated.balanceSheet.totalEquity, displayFormat, tableAutoUnit)}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Parse Errors */}
              {processedData.parseErrors.length > 0 && (
                <Card className="border-warning">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-warning">
                      <AlertCircle className="h-4 w-4" />
                      Varningar vid parsing ({processedData.parseErrors.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1 max-h-32 overflow-y-auto text-sm text-muted-foreground">
                      {processedData.parseErrors.map((error, idx) => (
                        <p key={idx}>{error}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Income Statement Tab - Monthly breakdown */}
        <TabsContent value="income-statement" className="space-y-4">
          {processedData && (() => {
            // Calculate discrepancy between monthly sum and annual total
            const monthlyData = processedData.aggregated.incomeStatementByMonth || {};
            const totalMonthlyRevenue = Object.values(monthlyData).reduce((sum, m) => sum + (m.revenue || 0), 0);
            const totalMonthlyExpenses = Object.values(monthlyData).reduce((sum, m) => sum + (m.expenses || 0), 0);
            const annualRevenue = processedData.aggregated.incomeStatement.totalRevenue;
            const annualExpenses = Math.abs(processedData.aggregated.incomeStatement.totalExpenses);
            const revenueDiscrepancy = annualRevenue - totalMonthlyRevenue;
            const expenseDiscrepancy = annualExpenses - totalMonthlyExpenses;
            const hasSignificantDiscrepancy = Math.abs(revenueDiscrepancy) > 100 || Math.abs(expenseDiscrepancy) > 100;
            
            return (
              <>
                {hasSignificantDiscrepancy && (
                  <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800 dark:text-amber-300">Avvikelse mellan månadsdata och årstotal</p>
                      <p className="text-amber-700 dark:text-amber-400/80 mt-1">
                        Summan av månadsdata skiljer sig från årstotalen 
                        {Math.abs(revenueDiscrepancy) > 100 && ` (intäkter: ${formatAmountWithDisplayFormat(revenueDiscrepancy, displayFormat, tableAutoUnit)})`}
                        {Math.abs(expenseDiscrepancy) > 100 && ` (kostnader: ${formatAmountWithDisplayFormat(expenseDiscrepancy, displayFormat, tableAutoUnit)})`}.
                        Detta kan bero på bokslutsjusteringar, ingående balanser på resultatkonton, eller poster utan verifikationer.
                      </p>
                    </div>
                  </div>
                )}
                <Card>
                  <CardHeader>
                    <CardTitle>Resultaträkning per månad</CardTitle>
                    <CardDescription>
                      Månadsvis uppdelning av intäkter och kostnader med årstotal
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                <ScrollArea className="w-full">
                  <div className="min-w-[800px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[200px] sticky left-0 bg-background z-10">Konto</TableHead>
                          {sortedMonths.map(month => (
                            <TableHead key={month} className="text-right min-w-[110px]">
                              {getMonthShortName(month)}
                            </TableHead>
                          ))}
                          <TableHead className="text-right min-w-[130px] font-bold bg-muted/50">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* ===== RÖRELSEINTÄKTER (3xxx) ===== */}
                        <TableRow className="bg-green-50/50 dark:bg-green-950/20">
                          <TableCell colSpan={sortedMonths.length + 2} className="font-semibold text-green-700 dark:text-green-400">
                            Rörelseintäkter (3xxx)
                          </TableCell>
                        </TableRow>
                        {allAccounts.revenue.map(account => {
                          const monthlyData = processedData.aggregated.incomeStatementByAccountAndMonth?.[account.account] || {};
                          return (
                            <TableRow key={account.account} className="hover:bg-muted/30">
                              <TableCell 
                                className="sticky left-0 bg-background z-10 cursor-pointer hover:bg-muted/50 transition-colors"
                                onClick={() => handleAccountClick(account.account, account.name)}
                              >
                                <span className="font-mono text-xs mr-2">{account.account}</span>
                                <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{account.name}</span>
                              </TableCell>
                              {sortedMonths.map(month => (
                                <TableCell key={month} className="text-right tabular-nums text-sm whitespace-nowrap">
                                  {monthlyData[month] ? formatAmountWithDisplayFormat(Math.abs(monthlyData[month]), displayFormat, tableAutoUnit) : '-'}
                                </TableCell>
                              ))}
                              <TableCell className="text-right font-medium tabular-nums bg-muted/50 whitespace-nowrap">
                                {formatAmountWithDisplayFormat(Math.abs(account.amount), displayFormat, tableAutoUnit)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Summa rörelseintäkter */}
                        <TableRow className="border-t-2">
                          <TableCell className="font-medium sticky left-0 bg-background z-10">Summa rörelseintäkter</TableCell>
                          {sortedMonths.map(month => {
                            const monthData = processedData.aggregated.incomeStatementByMonth?.[month];
                            return (
                              <TableCell key={month} className="text-right font-medium text-green-600 tabular-nums whitespace-nowrap">
                                {monthData ? formatAmountWithDisplayFormat(monthData.revenue, displayFormat, tableAutoUnit) : '-'}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right font-bold text-green-600 tabular-nums bg-muted/50 whitespace-nowrap">
                            {formatAmountWithDisplayFormat(processedData.aggregated.incomeStatement.totalRevenue, displayFormat, tableAutoUnit)}
                          </TableCell>
                        </TableRow>

                        {/* ===== RÖRELSEKOSTNADER (4xxx-7xxx) ===== */}
                        <TableRow className="bg-red-50/50 dark:bg-red-950/20">
                          <TableCell colSpan={sortedMonths.length + 2} className="font-semibold text-red-700 dark:text-red-400">
                            Rörelsekostnader (4xxx-7xxx)
                          </TableCell>
                        </TableRow>
                        {allAccounts.expenses.map(account => {
                          const monthlyData = processedData.aggregated.incomeStatementByAccountAndMonth?.[account.account] || {};
                          return (
                            <TableRow key={account.account} className="hover:bg-muted/30">
                              <TableCell 
                                className="sticky left-0 bg-background z-10 cursor-pointer hover:bg-muted/50 transition-colors"
                                onClick={() => handleAccountClick(account.account, account.name)}
                              >
                                <span className="font-mono text-xs mr-2">{account.account}</span>
                                <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{account.name}</span>
                              </TableCell>
                              {sortedMonths.map(month => (
                                <TableCell key={month} className="text-right tabular-nums text-sm whitespace-nowrap">
                                  {monthlyData[month] ? formatAmountWithDisplayFormat(Math.abs(monthlyData[month]), displayFormat, tableAutoUnit) : '-'}
                                </TableCell>
                              ))}
                              <TableCell className="text-right font-medium tabular-nums bg-muted/50 whitespace-nowrap">
                                {formatAmountWithDisplayFormat(Math.abs(account.amount), displayFormat, tableAutoUnit)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Summa rörelsekostnader */}
                        <TableRow className="border-t-2">
                          <TableCell className="font-medium sticky left-0 bg-background z-10">Summa rörelsekostnader</TableCell>
                          {sortedMonths.map(month => {
                            const monthData = processedData.aggregated.incomeStatementByMonth?.[month];
                            return (
                              <TableCell key={month} className="text-right font-medium text-red-600 tabular-nums whitespace-nowrap">
                                {monthData ? `-${formatAmountWithDisplayFormat(monthData.expenses, displayFormat, tableAutoUnit)}` : '-'}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right font-bold text-red-600 tabular-nums bg-muted/50 whitespace-nowrap">
                            {formatAmountWithDisplayFormat(-processedData.aggregated.incomeStatement.totalExpenses, displayFormat, tableAutoUnit)}
                          </TableCell>
                        </TableRow>

                        {/* ===== RÖRELSERESULTAT ===== */}
                        <TableRow className="border-t-4 bg-muted/50">
                          <TableCell className="font-bold sticky left-0 bg-muted/50 z-10">RÖRELSERESULTAT</TableCell>
                          {sortedMonths.map(month => {
                            const monthData = processedData.aggregated.incomeStatementByMonth?.[month];
                            const result = monthData?.operatingResult ?? 0;
                            return (
                              <TableCell key={month} className={`text-right font-bold tabular-nums whitespace-nowrap ${result >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {monthData ? formatAmountWithDisplayFormat(result, displayFormat, tableAutoUnit) : '-'}
                              </TableCell>
                            );
                          })}
                          <TableCell className={`text-right font-bold tabular-nums bg-muted/70 whitespace-nowrap ${
                            processedData.aggregated.incomeStatement.operatingResult >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatAmountWithDisplayFormat(processedData.aggregated.incomeStatement.operatingResult, displayFormat, tableAutoUnit)}
                          </TableCell>
                        </TableRow>

                        {/* ===== FINANSIELLA INTÄKTER (80xx-83xx) ===== */}
                        {allAccounts.financialIncome.length > 0 && (
                          <>
                            <TableRow className="bg-blue-50/50 dark:bg-blue-950/20">
                              <TableCell colSpan={sortedMonths.length + 2} className="font-semibold text-blue-700 dark:text-blue-400">
                                Finansiella intäkter (80xx-83xx)
                              </TableCell>
                            </TableRow>
                            {allAccounts.financialIncome.map(account => {
                              const monthlyData = processedData.aggregated.incomeStatementByAccountAndMonth?.[account.account] || {};
                              return (
                                <TableRow key={account.account} className="hover:bg-muted/30">
                                  <TableCell 
                                    className="sticky left-0 bg-background z-10 cursor-pointer hover:bg-muted/50 transition-colors"
                                    onClick={() => handleAccountClick(account.account, account.name)}
                                  >
                                    <span className="font-mono text-xs mr-2">{account.account}</span>
                                    <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{account.name}</span>
                                  </TableCell>
                                  {sortedMonths.map(month => (
                                    <TableCell key={month} className="text-right tabular-nums text-sm whitespace-nowrap">
                                      {monthlyData[month] ? formatAmountWithDisplayFormat(Math.abs(monthlyData[month]), displayFormat, tableAutoUnit) : '-'}
                                    </TableCell>
                                  ))}
                                  <TableCell className="text-right font-medium tabular-nums bg-muted/50 whitespace-nowrap">
                                    {formatAmountWithDisplayFormat(Math.abs(account.amount), displayFormat, tableAutoUnit)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow className="border-t">
                              <TableCell className="font-medium sticky left-0 bg-background z-10 text-sm">Summa finansiella intäkter</TableCell>
                              {sortedMonths.map(month => {
                                const monthData = processedData.aggregated.incomeStatementByMonth?.[month];
                                return (
                                  <TableCell key={month} className="text-right font-medium text-blue-600 tabular-nums text-sm whitespace-nowrap">
                                    {monthData?.financialIncome ? formatAmountWithDisplayFormat(monthData.financialIncome, displayFormat, tableAutoUnit) : '-'}
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-right font-bold text-blue-600 tabular-nums bg-muted/50 whitespace-nowrap">
                                {formatAmountWithDisplayFormat(processedData.aggregated.incomeStatement.totalFinancialIncome, displayFormat, tableAutoUnit)}
                              </TableCell>
                            </TableRow>
                          </>
                        )}

                        {/* ===== FINANSIELLA KOSTNADER (84xx) ===== */}
                        {allAccounts.financialExpenses.length > 0 && (
                          <>
                            <TableRow className="bg-orange-50/50 dark:bg-orange-950/20">
                              <TableCell colSpan={sortedMonths.length + 2} className="font-semibold text-orange-700 dark:text-orange-400">
                                Finansiella kostnader (84xx)
                              </TableCell>
                            </TableRow>
                            {allAccounts.financialExpenses.map(account => {
                              const monthlyData = processedData.aggregated.incomeStatementByAccountAndMonth?.[account.account] || {};
                              return (
                                <TableRow key={account.account} className="hover:bg-muted/30">
                                  <TableCell 
                                    className="sticky left-0 bg-background z-10 cursor-pointer hover:bg-muted/50 transition-colors"
                                    onClick={() => handleAccountClick(account.account, account.name)}
                                  >
                                    <span className="font-mono text-xs mr-2">{account.account}</span>
                                    <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{account.name}</span>
                                  </TableCell>
                                  {sortedMonths.map(month => (
                                    <TableCell key={month} className="text-right tabular-nums text-sm whitespace-nowrap">
                                      {monthlyData[month] ? formatAmountWithDisplayFormat(Math.abs(monthlyData[month]), displayFormat, tableAutoUnit) : '-'}
                                    </TableCell>
                                  ))}
                                  <TableCell className="text-right font-medium tabular-nums bg-muted/50 whitespace-nowrap">
                                    {formatAmountWithDisplayFormat(Math.abs(account.amount), displayFormat, tableAutoUnit)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow className="border-t">
                              <TableCell className="font-medium sticky left-0 bg-background z-10 text-sm">Summa finansiella kostnader</TableCell>
                              {sortedMonths.map(month => {
                                const monthData = processedData.aggregated.incomeStatementByMonth?.[month];
                                return (
                                  <TableCell key={month} className="text-right font-medium text-orange-600 tabular-nums text-sm whitespace-nowrap">
                                    {monthData?.financialExpenses ? `-${formatAmountWithDisplayFormat(monthData.financialExpenses, displayFormat, tableAutoUnit)}` : '-'}
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-right font-bold text-orange-600 tabular-nums bg-muted/50 whitespace-nowrap">
                                {formatAmountWithDisplayFormat(-processedData.aggregated.incomeStatement.totalFinancialExpenses, displayFormat, tableAutoUnit)}
                              </TableCell>
                            </TableRow>
                          </>
                        )}

                        {/* ===== RESULTAT EFTER FINANSIELLA POSTER ===== */}
                        {(allAccounts.financialIncome.length > 0 || allAccounts.financialExpenses.length > 0) && (
                          <TableRow className="border-t-4 bg-muted/50">
                            <TableCell className="font-bold sticky left-0 bg-muted/50 z-10">RESULTAT EFTER FINANSIELLA POSTER</TableCell>
                            {sortedMonths.map(month => {
                              const monthData = processedData.aggregated.incomeStatementByMonth?.[month];
                              const result = monthData?.resultAfterFinancial ?? 0;
                              return (
                                <TableCell key={month} className={`text-right font-bold tabular-nums whitespace-nowrap ${result >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {monthData ? formatAmountWithDisplayFormat(result, displayFormat, tableAutoUnit) : '-'}
                                </TableCell>
                              );
                            })}
                            <TableCell className={`text-right font-bold tabular-nums bg-muted/70 whitespace-nowrap ${
                              processedData.aggregated.incomeStatement.resultAfterFinancial >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatAmountWithDisplayFormat(processedData.aggregated.incomeStatement.resultAfterFinancial, displayFormat, tableAutoUnit)}
                            </TableCell>
                          </TableRow>
                        )}

                        {/* ===== BOKSLUTSDISPOSITIONER (88xx) ===== */}
                        {allAccounts.appropriations.length > 0 && (
                          <>
                            <TableRow className="bg-purple-50/50 dark:bg-purple-950/20">
                              <TableCell colSpan={sortedMonths.length + 2} className="font-semibold text-purple-700 dark:text-purple-400">
                                Bokslutsdispositioner (88xx)
                              </TableCell>
                            </TableRow>
                            {allAccounts.appropriations.map(account => {
                              const monthlyData = processedData.aggregated.incomeStatementByAccountAndMonth?.[account.account] || {};
                              return (
                                <TableRow key={account.account} className="hover:bg-muted/30">
                                  <TableCell 
                                    className="sticky left-0 bg-background z-10 cursor-pointer hover:bg-muted/50 transition-colors"
                                    onClick={() => handleAccountClick(account.account, account.name)}
                                  >
                                    <span className="font-mono text-xs mr-2">{account.account}</span>
                                    <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{account.name}</span>
                                  </TableCell>
                                  {sortedMonths.map(month => (
                                    <TableCell key={month} className="text-right tabular-nums text-sm whitespace-nowrap">
                                      {monthlyData[month] ? formatAmountWithDisplayFormat(monthlyData[month], displayFormat, tableAutoUnit) : '-'}
                                    </TableCell>
                                  ))}
                                  <TableCell className="text-right font-medium tabular-nums bg-muted/50 whitespace-nowrap">
                                    {formatAmountWithDisplayFormat(account.amount, displayFormat, tableAutoUnit)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow className="border-t">
                              <TableCell className="font-medium sticky left-0 bg-background z-10 text-sm">Summa bokslutsdispositioner</TableCell>
                              {sortedMonths.map(month => {
                                const monthData = processedData.aggregated.incomeStatementByMonth?.[month];
                                return (
                                  <TableCell key={month} className="text-right font-medium text-purple-600 tabular-nums text-sm whitespace-nowrap">
                                    {monthData?.appropriations ? formatAmountWithDisplayFormat(-monthData.appropriations, displayFormat, tableAutoUnit) : '-'}
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-right font-bold text-purple-600 tabular-nums bg-muted/50 whitespace-nowrap">
                                {formatAmountWithDisplayFormat(-processedData.aggregated.incomeStatement.totalAppropriations, displayFormat, tableAutoUnit)}
                              </TableCell>
                            </TableRow>
                          </>
                        )}

                        {/* ===== SKATT PÅ ÅRETS RESULTAT (89xx) ===== */}
                        {allAccounts.taxes.length > 0 && (
                          <>
                            <TableRow className="bg-gray-100/50 dark:bg-gray-800/30">
                              <TableCell colSpan={sortedMonths.length + 2} className="font-semibold text-gray-700 dark:text-gray-400">
                                Skatt på årets resultat (89xx)
                              </TableCell>
                            </TableRow>
                            {allAccounts.taxes.map(account => {
                              const monthlyData = processedData.aggregated.incomeStatementByAccountAndMonth?.[account.account] || {};
                              return (
                                <TableRow key={account.account} className="hover:bg-muted/30">
                                  <TableCell 
                                    className="sticky left-0 bg-background z-10 cursor-pointer hover:bg-muted/50 transition-colors"
                                    onClick={() => handleAccountClick(account.account, account.name)}
                                  >
                                    <span className="font-mono text-xs mr-2">{account.account}</span>
                                    <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{account.name}</span>
                                  </TableCell>
                                  {sortedMonths.map(month => (
                                    <TableCell key={month} className="text-right tabular-nums text-sm whitespace-nowrap">
                                      {monthlyData[month] ? formatAmountWithDisplayFormat(Math.abs(monthlyData[month]), displayFormat, tableAutoUnit) : '-'}
                                    </TableCell>
                                  ))}
                                  <TableCell className="text-right font-medium tabular-nums bg-muted/50 whitespace-nowrap">
                                    {formatAmountWithDisplayFormat(Math.abs(account.amount), displayFormat, tableAutoUnit)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow className="border-t">
                              <TableCell className="font-medium sticky left-0 bg-background z-10 text-sm">Summa skatt</TableCell>
                              {sortedMonths.map(month => {
                                const monthData = processedData.aggregated.incomeStatementByMonth?.[month];
                                return (
                                  <TableCell key={month} className="text-right font-medium text-gray-600 tabular-nums text-sm whitespace-nowrap">
                                    {monthData?.taxes ? `-${formatAmountWithDisplayFormat(monthData.taxes, displayFormat, tableAutoUnit)}` : '-'}
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-right font-bold text-gray-600 tabular-nums bg-muted/50 whitespace-nowrap">
                                {formatAmountWithDisplayFormat(-processedData.aggregated.incomeStatement.totalTaxes, displayFormat, tableAutoUnit)}
                              </TableCell>
                            </TableRow>
                          </>
                        )}

                        {/* ===== ÅRETS RESULTAT ===== */}
                        <TableRow className="border-t-4 bg-primary/10">
                          <TableCell className="font-bold text-lg sticky left-0 bg-primary/10 z-10">ÅRETS RESULTAT</TableCell>
                          {sortedMonths.map(month => {
                            const monthData = processedData.aggregated.incomeStatementByMonth?.[month];
                            const netResult = monthData?.netResult ?? 0;
                            return (
                              <TableCell 
                                key={month} 
                                className={`text-right font-bold tabular-nums whitespace-nowrap ${netResult >= 0 ? 'text-green-600' : 'text-red-600'}`}
                              >
                                {monthData ? formatAmountWithDisplayFormat(netResult, displayFormat, tableAutoUnit) : '-'}
                              </TableCell>
                            );
                          })}
                          <TableCell 
                            className={`text-right font-bold text-lg tabular-nums bg-primary/20 whitespace-nowrap ${
                              processedData.aggregated.incomeStatement.netResult >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {formatAmountWithDisplayFormat(processedData.aggregated.incomeStatement.netResult, displayFormat, tableAutoUnit)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </CardContent>
            </Card>
              </>
            );
          })()}
        </TabsContent>

        {/* Balance Sheet Tab */}
        <TabsContent value="balance-sheet" className="space-y-4">
          {processedData && (
            <>
              {/* Balance Indicator */}
              <Card className={`border-2 ${
                processedData.aggregated.balanceSheet.isBalanced 
                  ? 'border-green-500 bg-green-50/50 dark:bg-green-950/20' 
                  : 'border-red-500 bg-red-50/50 dark:bg-red-950/20'
              }`}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {processedData.aggregated.balanceSheet.isBalanced ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      )}
                      <span className="font-medium">
                        {processedData.aggregated.balanceSheet.isBalanced 
                          ? 'Balansräkningen balanserar' 
                          : 'Balansräkningen balanserar inte'}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Differens: </span>
                      <span className={`font-medium ${
                        Math.abs(processedData.aggregated.balanceSheet.balanceDifference || 0) < 1 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {formatAmountWithDisplayFormat(processedData.aggregated.balanceSheet.balanceDifference || 0, displayFormat, tableAutoUnit)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid gap-4 md:grid-cols-2">
                {/* Assets */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Tillgångar</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Fixed Assets (10xx-12xx) */}
                    {(processedData.aggregated.balanceSheet.fixedAssets?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Anläggningstillgångar (10-12xx)</h4>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {processedData.aggregated.balanceSheet.fixedAssets?.map(item => (
                            <div 
                              key={item.account} 
                              className="flex justify-between text-sm py-1 hover:bg-muted/50 px-2 rounded cursor-pointer transition-colors"
                              onClick={() => handleAccountClick(item.account, item.name)}
                            >
                              <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{item.account} {item.name}</span>
                              <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(item.amount, displayFormat, tableAutoUnit)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-sm font-medium mt-1 pt-1 border-t border-dashed">
                          <span>Summa anläggningstillgångar</span>
                          <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(processedData.aggregated.balanceSheet.totalFixedAssets || 0, displayFormat, tableAutoUnit)}</span>
                        </div>
                      </div>
                    )}

                    {/* Financial Assets (13xx) */}
                    {(processedData.aggregated.balanceSheet.financialAssets?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Finansiella anläggningstillgångar (13xx)</h4>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {processedData.aggregated.balanceSheet.financialAssets?.map(item => (
                            <div 
                              key={item.account} 
                              className="flex justify-between text-sm py-1 hover:bg-muted/50 px-2 rounded cursor-pointer transition-colors"
                              onClick={() => handleAccountClick(item.account, item.name)}
                            >
                              <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{item.account} {item.name}</span>
                              <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(item.amount, displayFormat, tableAutoUnit)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-sm font-medium mt-1 pt-1 border-t border-dashed">
                          <span>Summa finansiella tillgångar</span>
                          <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(processedData.aggregated.balanceSheet.totalFinancialAssets || 0, displayFormat, tableAutoUnit)}</span>
                        </div>
                      </div>
                    )}

                    {/* Current Assets (14xx-19xx) */}
                    {(processedData.aggregated.balanceSheet.currentAssets?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Omsättningstillgångar (14-19xx)</h4>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {processedData.aggregated.balanceSheet.currentAssets?.map(item => (
                            <div 
                              key={item.account} 
                              className="flex justify-between text-sm py-1 hover:bg-muted/50 px-2 rounded cursor-pointer transition-colors"
                              onClick={() => handleAccountClick(item.account, item.name)}
                            >
                              <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{item.account} {item.name}</span>
                              <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(item.amount, displayFormat, tableAutoUnit)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-sm font-medium mt-1 pt-1 border-t border-dashed">
                          <span>Summa omsättningstillgångar</span>
                          <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(processedData.aggregated.balanceSheet.totalCurrentAssets || 0, displayFormat, tableAutoUnit)}</span>
                        </div>
                      </div>
                    )}

                    {/* Fallback to legacy assets if new structure not available */}
                    {!(processedData.aggregated.balanceSheet.fixedAssets?.length || 
                       processedData.aggregated.balanceSheet.financialAssets?.length || 
                       processedData.aggregated.balanceSheet.currentAssets?.length) && (
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {processedData.aggregated.balanceSheet.assets.map(item => (
                          <div 
                            key={item.account} 
                            className="flex justify-between text-sm py-1 hover:bg-muted/50 px-2 rounded cursor-pointer transition-colors"
                            onClick={() => handleAccountClick(item.account, item.name)}
                          >
                            <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{item.account} {item.name}</span>
                            <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(item.amount, displayFormat, tableAutoUnit)}</span>
                          </div>
                        ))}
                        {processedData.aggregated.balanceSheet.assets.length === 0 && (
                          <p className="text-sm text-muted-foreground italic">Inga tillgångskonton hittades</p>
                        )}
                      </div>
                    )}

                    <Separator className="my-2" />

                    <div className="flex justify-between font-bold text-lg">
                      <span>SUMMA TILLGÅNGAR</span>
                      <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(processedData.aggregated.balanceSheet.totalAssets, displayFormat, tableAutoUnit)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Equity & Liabilities */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Eget kapital & Skulder</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Equity (20xx) */}
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-2">Eget kapital (20xx)</h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {processedData.aggregated.balanceSheet.equity.map(item => (
                          <div 
                            key={item.account} 
                            className="flex justify-between text-sm py-1 hover:bg-muted/50 px-2 rounded cursor-pointer transition-colors"
                            onClick={() => handleAccountClick(item.account, item.name)}
                          >
                            <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{item.account} {item.name}</span>
                            <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(item.amount), displayFormat, tableAutoUnit)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between text-sm font-medium mt-1 pt-1 border-t border-dashed">
                        <span>Summa eget kapital</span>
                        <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(processedData.aggregated.balanceSheet.totalEquity), displayFormat, tableAutoUnit)}</span>
                      </div>
                    </div>

                    {/* Untaxed Reserves (21xx) */}
                    {(processedData.aggregated.balanceSheet.untaxedReserves?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Obeskattade reserver (21xx)</h4>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {processedData.aggregated.balanceSheet.untaxedReserves?.map(item => (
                            <div 
                              key={item.account} 
                              className="flex justify-between text-sm py-1 hover:bg-muted/50 px-2 rounded cursor-pointer transition-colors"
                              onClick={() => handleAccountClick(item.account, item.name)}
                            >
                              <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{item.account} {item.name}</span>
                              <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(item.amount), displayFormat, tableAutoUnit)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-sm font-medium mt-1 pt-1 border-t border-dashed">
                          <span>Summa obeskattade reserver</span>
                          <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(processedData.aggregated.balanceSheet.totalUntaxedReserves || 0), displayFormat, tableAutoUnit)}</span>
                        </div>
                      </div>
                    )}

                    {/* Provisions (22xx) */}
                    {(processedData.aggregated.balanceSheet.provisions?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Avsättningar (22xx)</h4>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {processedData.aggregated.balanceSheet.provisions?.map(item => (
                            <div 
                              key={item.account} 
                              className="flex justify-between text-sm py-1 hover:bg-muted/50 px-2 rounded cursor-pointer transition-colors"
                              onClick={() => handleAccountClick(item.account, item.name)}
                            >
                              <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{item.account} {item.name}</span>
                              <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(item.amount), displayFormat, tableAutoUnit)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-sm font-medium mt-1 pt-1 border-t border-dashed">
                          <span>Summa avsättningar</span>
                          <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(processedData.aggregated.balanceSheet.totalProvisions || 0), displayFormat, tableAutoUnit)}</span>
                        </div>
                      </div>
                    )}

                    {/* Long-term Liabilities (23xx) */}
                    {(processedData.aggregated.balanceSheet.longTermLiabilities?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Långfristiga skulder (23xx)</h4>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {processedData.aggregated.balanceSheet.longTermLiabilities?.map(item => (
                            <div 
                              key={item.account} 
                              className="flex justify-between text-sm py-1 hover:bg-muted/50 px-2 rounded cursor-pointer transition-colors"
                              onClick={() => handleAccountClick(item.account, item.name)}
                            >
                              <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{item.account} {item.name}</span>
                              <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(item.amount), displayFormat, tableAutoUnit)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-sm font-medium mt-1 pt-1 border-t border-dashed">
                          <span>Summa långfristiga skulder</span>
                          <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(processedData.aggregated.balanceSheet.totalLongTermLiabilities || 0), displayFormat, tableAutoUnit)}</span>
                        </div>
                      </div>
                    )}

                    {/* Short-term Liabilities (24xx-29xx) */}
                    {(processedData.aggregated.balanceSheet.shortTermLiabilities?.length ?? 0) > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Kortfristiga skulder (24-29xx)</h4>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {processedData.aggregated.balanceSheet.shortTermLiabilities?.map(item => (
                            <div 
                              key={item.account} 
                              className="flex justify-between text-sm py-1 hover:bg-muted/50 px-2 rounded cursor-pointer transition-colors"
                              onClick={() => handleAccountClick(item.account, item.name)}
                            >
                              <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{item.account} {item.name}</span>
                              <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(item.amount), displayFormat, tableAutoUnit)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-sm font-medium mt-1 pt-1 border-t border-dashed">
                          <span>Summa kortfristiga skulder</span>
                          <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(processedData.aggregated.balanceSheet.totalShortTermLiabilities || 0), displayFormat, tableAutoUnit)}</span>
                        </div>
                      </div>
                    )}

                    {/* Fallback to legacy liabilities if new structure not available */}
                    {!(processedData.aggregated.balanceSheet.untaxedReserves?.length || 
                       processedData.aggregated.balanceSheet.provisions?.length || 
                       processedData.aggregated.balanceSheet.longTermLiabilities?.length || 
                       processedData.aggregated.balanceSheet.shortTermLiabilities?.length) && 
                      processedData.aggregated.balanceSheet.liabilities.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Skulder</h4>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {processedData.aggregated.balanceSheet.liabilities.map(item => (
                            <div 
                              key={item.account} 
                              className="flex justify-between text-sm py-1 hover:bg-muted/50 px-2 rounded cursor-pointer transition-colors"
                              onClick={() => handleAccountClick(item.account, item.name)}
                            >
                              <span className="text-muted-foreground underline decoration-dotted decoration-muted-foreground/50 hover:decoration-primary">{item.account} {item.name}</span>
                              <span className="font-medium whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(item.amount), displayFormat, tableAutoUnit)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-sm font-medium mt-1">
                          <span>Summa skulder</span>
                          <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(processedData.aggregated.balanceSheet.totalLiabilities), displayFormat, tableAutoUnit)}</span>
                        </div>
                      </div>
                    )}

                    <Separator className="my-2" />

                    <div className="flex justify-between font-bold text-lg">
                      <span>SUMMA EK & SKULDER</span>
                      <span className="whitespace-nowrap">{formatAmountWithDisplayFormat(Math.abs(processedData.aggregated.balanceSheet.totalEquityAndLiabilities || 
                        (processedData.aggregated.balanceSheet.totalEquity + processedData.aggregated.balanceSheet.totalLiabilities)), displayFormat, tableAutoUnit)}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Comparison Tab */}
        <TabsContent value="comparison" className="space-y-6">
          {processedData && previousYearData && (
            <>
              <VarianceAnalysis 
                currentYear={processedData} 
                previousYear={previousYearData} 
                displayFormat={displayFormat}
                thresholds={materialityThresholds}
                auditId={auditId}
              />
              <YearComparisonView currentYear={processedData} previousYear={previousYearData} displayFormat={displayFormat} />
              <TrendCharts currentYear={processedData} previousYear={previousYearData} displayFormat={displayFormat} />
              <KeyRatiosComparison currentYear={processedData} previousYear={previousYearData} />
            </>
          )}
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="statistics" className="space-y-4">
          {processedData && (
            <div className="grid gap-4 md:grid-cols-2">
              {/* Summary Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Sammanfattning</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Antal konton</span>
                    <span className="font-medium">{processedData.aggregated.statistics.totalAccounts}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Antal verifikationer</span>
                    <span className="font-medium">{processedData.aggregated.statistics.totalVouchers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Antal transaktioner</span>
                    <span className="font-medium">{processedData.aggregated.statistics.totalTransactions}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Vouchers by Series */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Verifikationer per serie</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(processedData.aggregated.statistics.vouchersBySeries).map(([series, count]) => (
                      <div key={series} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Serie {series}</span>
                        <span className="font-medium">{count} st</span>
                      </div>
                    ))}
                    {Object.keys(processedData.aggregated.statistics.vouchersBySeries).length === 0 && (
                      <p className="text-sm text-muted-foreground italic">Ingen verifikationsdata tillgänglig</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Vouchers by Month - Clickable */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base">Verifikationer per månad</CardTitle>
                  <CardDescription>
                    Klicka på en månad för att visa verifikationer
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2 grid-cols-2 md:grid-cols-4 lg:grid-cols-6">
                    {Object.entries(processedData.aggregated.statistics.vouchersByMonth)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([month, count]) => (
                        <button
                          key={month}
                          onClick={() => handleMonthClick(month)}
                          className="flex justify-between text-sm p-3 bg-muted/30 rounded-lg hover:bg-primary/10 hover:border-primary/30 border border-transparent transition-colors cursor-pointer text-left"
                        >
                          <span className="text-muted-foreground">{month}</span>
                          <span className="font-medium">{count}</span>
                        </button>
                      ))}
                    {Object.keys(processedData.aggregated.statistics.vouchersByMonth).length === 0 && (
                      <p className="text-sm text-muted-foreground italic col-span-full">Ingen månadsdata tillgänglig</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Cash Flow Tab */}
        <TabsContent value="cashflow" className="space-y-4">
          {processedData && previousYearData ? (
            <CashFlowAnalysis 
              currentYear={processedData} 
              previousYear={previousYearData}
              displayFormat={displayFormat}
            />
          ) : (
            <Card className="p-8 text-center">
              <ArrowLeftRight className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                Ladda upp jämförelseår för kassaflödesanalys
              </h3>
              <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                Kassaflödesanalysen kräver balansräkningsdata från två räkenskapsår 
                för att kunna beräkna förändring i rörelsekapital och likvida medel.
              </p>
              <Button 
                variant="outline" 
                onClick={() => setActiveTab('upload')}
              >
                <Upload className="h-4 w-4 mr-2" />
                Gå till uppladdning
              </Button>
            </Card>
          )}
        </TabsContent>

        {/* Analytical Review Tab */}
        <TabsContent value="analytical-review" className="space-y-4">
          {processedData ? (
            <AnalyticalReviewTab
              currentYear={processedData}
              previousYear={previousYearData || undefined}
              thresholds={materialityThresholds}
              auditId={auditId}
              displayFormat={displayFormat}
            />
          ) : (
            <Card className="p-8 text-center">
              <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                Ladda upp SIE-fil för analytisk granskning
              </h3>
              <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                Analytisk granskning kräver en uppladdad SIE-fil för att kunna 
                generera grafer och signaler.
              </p>
              <Button variant="outline" onClick={() => setActiveTab('upload')}>
                <Upload className="h-4 w-4 mr-2" />
                Gå till uppladdning
              </Button>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Voucher List Modal */}
      {selectedMonth && (
        <VoucherListModal
          isOpen={isVoucherModalOpen}
          onClose={() => {
            setIsVoucherModalOpen(false);
            setSelectedMonth(null);
          }}
          month={selectedMonth}
          vouchers={processedData?.vouchers || []}
          displayFormat={displayFormat}
          accountNames={accountNames}
        />
      )}

      {/* Account Vouchers Modal */}
      {selectedAccount && (
        <AccountVouchersModal
          isOpen={isAccountVouchersModalOpen}
          onClose={() => {
            setIsAccountVouchersModalOpen(false);
            setSelectedAccount(null);
          }}
          accountNumber={selectedAccount.account}
          accountName={selectedAccount.name}
          vouchers={processedData?.vouchers || []}
          displayFormat={displayFormat}
          accountNames={accountNames}
        />
      )}
    </div>
  );
}
